import type {CSSProperties} from 'react';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeUpsellLook = 'upsell-cta' | 'upsell-ghost' | 'upsell-email-pill' | 'upsell-email-dismiss';

const UPSELL_LOOKS = new Set<string>(['upsell-cta', 'upsell-ghost', 'upsell-email-pill', 'upsell-email-dismiss']);
export const isWedgeUpsellLook = (look: string | undefined): look is WedgeUpsellLook => look !== undefined && UPSELL_LOOKS.has(look);

/* Released route planning and Outreach upsell pages (a locked PITCH perk on the free plan). The unlock button is full width and 56px tall with a
   16px radius, no border, 16.5px type at weight 750 and -0.2 tracking in the Wedge font, a centered flex row with a 9px gap. The ghost button under
   the gallery is 48px tall with a 14px radius, a transparent fill, the accent ink, 15px bold type and an 8px gap. The brand fill, the glow, the
   unlock ink and the ghost edge are color strings the page builds from its roles (a gradient, a mix and a fallback), so the caller passes them
   in style. The caller owns the glyph and the labels. It draws no transparent target and no anchor (wedgeSettingsDrawsHit): each button is 48px
   tall or more and full width in a centered column, so a 44px region would only repeat the control.
   The AI email drafting nudge of the brief has two more: the price pill (accent ink on the accent tint, 11px bold type, 5px 10px padding, a full
   round radius, no border, no wrap) and the dismiss mark (no fill, no border, 12px padding in a 44px minimum box, the quaternary label ink, the
   manipulation touch action). The mark already owns the 44px box and the pill sits in a one line row, so neither draws a target. */
export function wedgeUpsellStyle(look: WedgeUpsellLook): CSSProperties {
  const r = wedgeExactRole;
  if (look === 'upsell-email-pill') return {
    background: r('accentTint'), color: r('accent'), border: 0, padding: '5px 10px', borderRadius: 999,
    fontSize: 11, fontWeight: 700, letterSpacing: 0.2, cursor: 'pointer', fontFamily: r('font'), whiteSpace: 'nowrap',
  };
  if (look === 'upsell-email-dismiss') return {
    background: 'transparent', border: 0, color: r('labelQuad'), padding: 12, minWidth: 44, minHeight: 44, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
  };
  if (look === 'upsell-cta') return {
    width: '100%', height: 56, borderRadius: 16, border: 0, cursor: 'pointer',
    fontFamily: r('font'), fontSize: 16.5, fontWeight: 750, letterSpacing: -0.2,
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9,
  };
  return {
    width: '100%', height: 48, borderRadius: 14, cursor: 'pointer', background: 'transparent',
    color: r('accent'), fontFamily: r('font'), fontSize: 15, fontWeight: 700,
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  };
}
